// @givmo/org-console — the organization's Profile PAGE (PE-WEB1; Profile Enrichment plan §1.4 /
// §2.4, D-PE-12): an ORG-LEVEL page on shell contract v3, never a module.
//
// ONE page at `/profile` on every class console, reached as the Organization section's `Profile`
// row inside whichever module frame it was opened from, with three IN-PAGE tabs — the page's own
// UI, never sidebar rows (the shell's exactly-one-active-row law):
//
//   1. **Details** — the class's identity fields. The HOST supplies this panel (its existing fact
//      editor, its existing operations and permissions), plus the impact mount slot its plan fills.
//   2. **Page content** — the block editor (`ProfileBlocksEditor`) over the host's owner-view /
//      replace / upload operations.
//   3. **Preview** — the public composition (`ProfilePreview`) over the host's public read.
//
// Two more sit between Page content and Preview when the host passes `brand` (Email Campaign plan
// EMC-WEB11; D-EMC-20 (c), (d); mockups 12a–12c): **Branding** (`OrgBrandingPanel`) and
// **Collateral** (`OrgCollateralPanel`), over the backend's org-generic `/org/{ref}/profile/…`
// family. With them the page takes the QR design page's 1200px column (the housed QR editor truncates
// its choices at 880) and its title and tabs stay in view as it scrolls; the original three tabs keep
// their 880px reading width inside it. Without `brand` the page is exactly what it was.
//
// The Details and Page content panels stay MOUNTED while another tab is active (`keepMounted`), so
// an admin who peeks at View as public mid-edit comes back to the draft, never to the served page
// (PE-WEB8). An inactive kept panel is `hidden` + `inert` (Base UI), out of the accessibility tree
// and the tab order. The preview panel is NOT kept: it holds no draft, and a kept preview would keep
// a clicked video iframe running while hidden.
//
// ══ AVAILABILITY IS INFERRED, NEVER READ FROM A FLAG (the feature-switch rule) ══════════════════
//
// The blocks tab renders the editor ONLY when the owner view answers. A 404 on that read is the
// server saying "page content is not switched on for this organization's class" (the entity-types
// allowlist, plan §2.7), and the tab shows the honest unavailable state — a sentence, no dead
// controls. This package never asks a `/config` or flag endpoint what is on; the operation's own
// answer is the only source (plan §1.4; PE-WEB1's Vitest pin).
//
// ══ ENTITY-GENERIC BY CONSTRUCTION ═════════════════════════════════════════════════════════════
//
// Keyed on `orgRef`, operations injected, words injected. This file names no class.

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import type { ProfilePageOut, ProfilePageReplaceIn } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  Tabs,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { LayoutTemplate } from 'lucide-react';
import { OrgBrandingPanel } from './org-branding-panel';
import { OrgCollateralPanel } from './org-collateral-panel';
import { PROFILE_COPY } from './profile-copy';
import { ProfileBlocksEditor } from './profile-blocks-editor';
import {
  readPageVersion,
  withExpectedVersion,
  type ProfilePageOperations,
  type ProfilePageVocab,
} from './profile-model';
import { ProfilePreview, type ProfilePreviewState, type PublicHeaderSlot } from './profile-preview';
import { readOwnerStatistics, type ProfileStatisticDefinition } from './profile-statistics';
import { ProfileStatisticsCard, type ProfileStatisticsVocab } from './profile-statistics-card';

export type ProfileTab = 'facts' | 'blocks' | 'branding' | 'collateral' | 'preview';
export const PROFILE_TABS: readonly ProfileTab[] = [
  'facts',
  'blocks',
  'branding',
  'collateral',
  'preview',
];
/** The two tabs a host opts into with `brand` (EMC-WEB11). */
const BRAND_TABS: readonly ProfileTab[] = ['branding', 'collateral'];

export function isProfileTab(value: unknown): value is ProfileTab {
  return typeof value === 'string' && (PROFILE_TABS as readonly string[]).includes(value);
}

/** The tab's word in the address (`?tab=`), deep-linkable — the tab labels' own words (D-PE-17 (7)). */
export const PROFILE_TAB_SEARCH = {
  facts: 'details',
  blocks: 'content',
  branding: 'branding',
  collateral: 'collateral',
  preview: 'public',
} as const satisfies Record<ProfileTab, string>;
export type ProfileTabSearch = (typeof PROFILE_TAB_SEARCH)[ProfileTab];

export function profileTabToSearch(tab: ProfileTab): ProfileTabSearch {
  return PROFILE_TAB_SEARCH[tab];
}

/** The tab an address names, or null for anything else (the host falls back to its default). */
export function profileTabFromSearch(value: unknown): ProfileTab | null {
  if (typeof value !== 'string') return null;
  const hit = (Object.keys(PROFILE_TAB_SEARCH) as ProfileTab[]).find(
    (tab) => PROFILE_TAB_SEARCH[tab] === value,
  );
  return hit ?? null;
}

/** The default test-id namespace. The page ROOT is always `page-profile` (the address's id). */
export const PROFILE_PAGE_TEST_ID_PREFIX = 'profile-page';

export interface OrgProfilePageProps {
  /** The organization this page is scoped to — the ONLY scope input. */
  orgRef: string;
  /** The host's four operations on its own plane. */
  operations: ProfilePageOperations;
  vocab: ProfilePageVocab;
  /** The class's identity-fields panel (the host's existing editor) — the Details tab. */
  factsPanel: ReactNode;
  /** The catalog-keyed mount slot another plan fills (the impact measures); absent ⇒ nothing. */
  impactSlot?: ReactNode;
  /**
   * The View-as-public tab's QR code card (PE-WEB11), directly under the public-page link or
   * statement — the host's `OrgPublicQrCard`; absent ⇒ no card (the `impactSlot` precedent).
   */
  publicQrSlot?: ReactNode;
  /**
   * The View-as-public tab's line on who can see the public page, under the public-page link or
   * statement — the host's door onto its visibility settings (EP-MNT5(c)); absent ⇒
   * none (the `publicQrSlot` precedent).
   */
  publicVisibilitySlot?: ReactNode;
  /** Whether this person may change page content — the host reads the standing read for it. */
  canEdit: boolean;
  /**
   * The class's PUBLIC header for the View-as-public tab, exactly as its public surface shows it —
   * rendered by the host from its own entity read (D-PE-17 (6)).
   */
  publicHeader?: PublicHeaderSlot;
  /** Told once per block of a kind this client does not know (§1.2) — the host's telemetry. */
  onUnknownKind?: (kind: string) => void;
  /**
   * The words for the class's statistics (PE-WEB15). Present ⇒ the Page content tab leads with the
   * Statistics card and View as public draws the figures under the header, whenever the owner read
   * carries a `statistics` object (a class whose catalog binds none carries `null`: nothing is drawn).
   */
  statisticsVocab?: ProfileStatisticsVocab;
  /** The host's gate, asked before a stat turns on (resolve `false` to leave it off). */
  onBeforeStatisticTurnOn?: (stat: ProfileStatisticDefinition) => Promise<boolean>;
  /** A host action after one withheld reason's note on the Statistics card. */
  statisticWithheldAction?: (
    reason: string,
    stats: readonly ProfileStatisticDefinition[],
  ) => ReactNode;
  /**
   * The Branding and Collateral tabs (EMC-WEB11). Present ⇒ both tabs, the wide column and the
   * sticky title; `qrDesignSlot` is the host's organization QR design editor, housed on Branding.
   * Absent ⇒ neither, and an address naming one opens Details. `stickyTop` is how far below the
   * viewport's top the title and tabs stop: the height of the host shell's own sticky bar, as CSS
   * (a CSS variable the host shell sets), since this package never learns which shell it is in.
   * `onUnsavedChange` is told when Branding starts or stops holding an edit not yet saved, for the
   * host's unsaved-changes guard (pass a stable function). `qrDesignUnsaved` is whether the housed QR
   * design holds an edit not yet saved, which only the host hears; Branding marks its sub-tab with it.
   */
  brand?: {
    qrDesignSlot?: ReactNode;
    qrDesignUnsaved?: boolean;
    stickyTop?: string;
    onUnsavedChange?: (unsaved: boolean) => void;
  };
  /** The active tab when the host mirrors it to the address; uncontrolled otherwise. */
  tab?: ProfileTab;
  defaultTab?: ProfileTab;
  onTabChange?: (tab: ProfileTab) => void;
  testIdPrefix?: string;
}

/** Whether an owner-read failure means "not available for this organization" (the 404 answer). */
export function isProfilePageUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

export function profilePageQueryKey(orgRef: string, view: 'owner' | 'public') {
  return ['org-profile', orgRef, view] as const;
}

export function OrgProfilePage({
  orgRef,
  operations,
  vocab,
  factsPanel,
  impactSlot,
  publicQrSlot,
  publicVisibilitySlot,
  canEdit,
  publicHeader,
  onUnknownKind,
  statisticsVocab,
  onBeforeStatisticTurnOn,
  statisticWithheldAction,
  brand,
  tab,
  defaultTab = 'facts',
  onTabChange,
  testIdPrefix = PROFILE_PAGE_TEST_ID_PREFIX,
}: OrgProfilePageProps) {
  const prefix = testIdPrefix;
  const queryClient = useQueryClient();
  const [ownTab, setOwnTab] = useState<ProfileTab>(defaultTab);
  const requestedTab = tab ?? ownTab;
  const activeTab =
    brand === undefined && BRAND_TABS.includes(requestedTab) ? 'facts' : requestedTab;
  const wide = brand !== undefined;
  const selectTab = (value: unknown) => {
    const next = isProfileTab(value) ? value : 'facts';
    setOwnTab(next);
    onTabChange?.(next);
  };

  const ownerKey = profilePageQueryKey(orgRef, 'owner');
  const publicKey = profilePageQueryKey(orgRef, 'public');

  // A 404 is the answer, not a blip — no retry.
  const owner = useQuery({
    queryKey: ownerKey,
    queryFn: () => operations.getOwnerPage(),
    retry: false,
  });
  const publicPage = useQuery({
    queryKey: publicKey,
    queryFn: () => operations.getPublicPage(),
    retry: false,
    enabled: activeTab === 'preview',
  });

  const replace = useMutation({
    mutationFn: (body: ProfilePageReplaceIn) => operations.replacePage(body),
    onSuccess: (page) => {
      queryClient.setQueryData(ownerKey, page);
      void queryClient.invalidateQueries({ queryKey: publicKey });
    },
  });

  // The Statistics card saves its list ALONE — the replace with `statistics` and no `blocks`, so the
  // block draft below is never part of that save (the backend reads an absent `blocks` as "unchanged").
  // It sends the version of the page it shows (PE-WEB18), read when the save starts, so a save that
  // follows another sends the version the first one brought back; and only when that page carried one.
  const ownerStatistics =
    statisticsVocab !== undefined && owner.data ? readOwnerStatistics(owner.data) : null;
  const saveStatistics = async (keys: string[]) => {
    const shown = queryClient.getQueryData<ProfilePageOut>(ownerKey);
    await replace.mutateAsync(withExpectedVersion({ statistics: keys }, readPageVersion(shown)));
  };
  // The Statistics choice changed after this page was read: show the latest one at once.
  const refetchOwner = () => void queryClient.invalidateQueries({ queryKey: ownerKey });

  // Reload page (PE-WEB18): the owner view read again, which then IS the page every part of this
  // screen shows. A read the page cannot survive (page content switched off, access gone) ends as a
  // load does; any other failure is the editor's to say.
  const reloadOwner = async (): Promise<ProfilePageOut> => {
    try {
      const latest = await operations.getOwnerPage();
      queryClient.setQueryData(ownerKey, latest);
      void queryClient.invalidateQueries({ queryKey: publicKey });
      return latest;
    } catch (error) {
      if (isGivmoError(error) && (error.status === 403 || error.status === 404)) refetchOwner();
      throw error;
    }
  };

  // Two honest empty states, both inferred from operations: the owner read's 404 says page content
  // is not switched on for this class (the same answer the blocks tab shows); a public 404 while the
  // owner read answers says the page is not served to anyone right now. The owner read is consulted
  // FIRST and waited for: while it is still pending the tab stays on the loading line, so a public
  // 404 that lands before a slow owner 404 never paints the suppression banner for a dark class.
  const previewState: ProfilePreviewState = owner.isPending
    ? { kind: 'loading' }
    : owner.isError && isProfilePageUnavailable(owner.error)
      ? { kind: 'unavailable' }
      : publicPage.isPending
        ? { kind: 'loading' }
        : publicPage.isError
          ? isProfilePageUnavailable(publicPage.error)
            ? { kind: 'suppressed' }
            : {
                kind: 'error',
                message: isGivmoError(publicPage.error)
                  ? publicPage.error.message
                  : PROFILE_COPY.preview.loadError,
                onRetry: () => void publicPage.refetch(),
              }
          : { kind: 'served', page: publicPage.data };

  const header = (
    <PageHeader
      title={PROFILE_COPY.page.title}
      subtitle={PROFILE_COPY.page.subtitle}
      organizationName={vocab.orgName}
      organizationNameTestId={`${prefix}-org-badge`}
    />
  );
  // A 44px tap target on a phone (PE-WEB12): the saas tab is 41px tall; the desktop row keeps its size.
  const tabList = (
    <Tabs.List aria-label={PROFILE_COPY.page.title}>
      <Tabs.Tab value="facts" className="max-lg:min-h-44" testId={`${prefix}-tab-facts`}>
        {PROFILE_COPY.tabs.facts}
      </Tabs.Tab>
      <Tabs.Tab value="blocks" className="max-lg:min-h-44" testId={`${prefix}-tab-blocks`}>
        {PROFILE_COPY.tabs.blocks}
      </Tabs.Tab>
      {brand !== undefined && (
        <>
          <Tabs.Tab value="branding" className="max-lg:min-h-44" testId={`${prefix}-tab-branding`}>
            {PROFILE_COPY.tabs.branding}
          </Tabs.Tab>
          <Tabs.Tab
            value="collateral"
            className="max-lg:min-h-44"
            testId={`${prefix}-tab-collateral`}
          >
            {PROFILE_COPY.tabs.collateral}
          </Tabs.Tab>
        </>
      )}
      <Tabs.Tab value="preview" className="max-lg:min-h-44" testId={`${prefix}-tab-preview`}>
        {PROFILE_COPY.tabs.preview}
      </Tabs.Tab>
    </Tabs.List>
  );
  // In the wide column the original tabs keep their reading width.
  const narrow = wide ? 'max-w-[880px]' : undefined;

  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col px-24',
        wide ? 'max-w-[1200px] pb-24' : 'max-w-[880px] gap-16 py-24',
      )}
      {...testIdProp('page-profile')}
    >
      {!wide && header}

      <Tabs.Root
        appearance="saas"
        value={activeTab}
        onValueChange={selectTab}
        testId={`${prefix}-tabs`}
      >
        {wide ? (
          // The title and tabs stay in view as the page scrolls (mockup 12a's build row), on the
          // page's own background so the cards pass under them.
          <div
            className="sticky z-10 -mx-24 flex flex-col gap-16 bg-surface-sunken px-24 pt-24 pb-16"
            style={{ top: brand?.stickyTop ?? 0 }}
            {...testIdProp(`${prefix}-sticky-header`)}
          >
            {header}
            {tabList}
          </div>
        ) : (
          tabList
        )}

        <Tabs.Panel value="facts" focusable={false} keepMounted className={narrow}>
          <div className="flex flex-col gap-16" {...testIdProp(`${prefix}-facts`)}>
            {factsPanel}
            {impactSlot !== undefined && impactSlot !== null && (
              <div {...testIdProp(`${prefix}-impact-slot`)}>{impactSlot}</div>
            )}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="blocks" focusable={false} keepMounted className={narrow}>
          {owner.isPending ? (
            <Card className="mt-16 p-24" testId={`${prefix}-loading`}>
              <div className="flex flex-col gap-10">
                <Skeleton className="h-16 w-1/3" />
                <Skeleton className="h-12 w-2/3" />
              </div>
            </Card>
          ) : owner.isError ? (
            isProfilePageUnavailable(owner.error) ? (
              <div className="pt-16">
                <EmptyState
                  testId={`${prefix}-unavailable`}
                  icon={<Icon icon={LayoutTemplate} size={20} aria-hidden />}
                  title={PROFILE_COPY.blocks.unavailable.title}
                  description={PROFILE_COPY.blocks.unavailable.body}
                />
              </div>
            ) : (
              <Card className="mt-16" testId={`${prefix}-error`}>
                <CardBody className="flex flex-col items-start gap-12">
                  <p className="font-sans text-s14 text-text-secondary">
                    {isGivmoError(owner.error)
                      ? owner.error.message
                      : PROFILE_COPY.blocks.errors.load}
                  </p>
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    onClick={() => void owner.refetch()}
                    testId={`${prefix}-retry`}
                  >
                    {PROFILE_COPY.common.retry}
                  </Button>
                </CardBody>
              </Card>
            )
          ) : (
            <div className="flex flex-col gap-16">
              {ownerStatistics !== null && statisticsVocab !== undefined && (
                <div className="pt-16">
                  <ProfileStatisticsCard
                    statistics={ownerStatistics}
                    canEdit={canEdit}
                    vocab={statisticsVocab}
                    onSave={saveStatistics}
                    onStale={refetchOwner}
                    onBeforeTurnOn={onBeforeStatisticTurnOn}
                    withheldAction={statisticWithheldAction}
                    testIdPrefix={prefix}
                  />
                </div>
              )}
              <ProfileBlocksEditor
                page={owner.data}
                canEdit={canEdit}
                vocab={vocab}
                onSave={(body) => replace.mutateAsync(body)}
                onReload={reloadOwner}
                onUploadImage={(file) => operations.uploadImage(file)}
                testIdPrefix={prefix}
              />
            </div>
          )}
        </Tabs.Panel>

        {brand !== undefined && (
          <>
            <Tabs.Panel value="branding" focusable={false} keepMounted>
              <OrgBrandingPanel
                orgRef={orgRef}
                orgName={vocab.orgName ?? PROFILE_COPY.branding.orgFallback}
                canEdit={canEdit}
                qrDesignSlot={brand.qrDesignSlot}
                qrDesignUnsaved={brand.qrDesignUnsaved}
                onUnsavedChange={brand.onUnsavedChange}
                testIdPrefix={prefix}
              />
            </Tabs.Panel>
            {/* Kept mounted, as Branding is: an upload still going shows its tile on return. */}
            <Tabs.Panel value="collateral" focusable={false} keepMounted>
              <OrgCollateralPanel orgRef={orgRef} canEdit={canEdit} testIdPrefix={prefix} />
            </Tabs.Panel>
          </>
        )}

        <Tabs.Panel value="preview" focusable={false} className={narrow}>
          <ProfilePreview
            state={previewState}
            vocab={vocab}
            header={publicHeader}
            publicQr={publicQrSlot}
            publicVisibility={publicVisibilitySlot}
            statistics={
              ownerStatistics !== null && statisticsVocab !== undefined
                ? {
                    figures: ownerStatistics.publicView,
                    withheld: ownerStatistics.withheld,
                    vocab: statisticsVocab,
                  }
                : undefined
            }
            onUnknownKind={onUnknownKind}
            testIdPrefix={prefix}
          />
        </Tabs.Panel>
      </Tabs.Root>
    </div>
  );
}
